import React, { useState, useEffect } from 'react';
import './Header.css';
import tiktokLogo from '../images/tiktok.svg';
import { trackCTA, trackMenuToggle, trackOutboundClick } from '../services/trackingService';

const Header = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Prevent body scroll when menu is open and close on Escape
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.classList.add('menu-open');
        } else {
            document.body.classList.remove('menu-open');
        }

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setIsMobileMenuOpen(false);
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isMobileMenuOpen]);

    // Close menu when resizing to desktop
    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth > 768 && isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [isMobileMenuOpen]);

    const toggleMobileMenu = () => {
        trackMenuToggle(!isMobileMenuOpen);
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const handleLinkClick = (targetId) => {
        if (targetId.startsWith('#')) {
            const id = targetId.substring(1);
            const element = document.getElementById(id);
            if (element) {
                const header = document.querySelector('.header');
                const offset = (header ? header.offsetHeight : 80) + 12;
                const top = element.getBoundingClientRect().top + window.scrollY - offset;
                window.scrollTo({ top, behavior: 'smooth' });
            }
        }
        setIsMobileMenuOpen(false);
    };

    return (
        <>
            {/* Header */}
            <header className={`header trans_400 ${isScrolled ? 'scrolled' : ''}`}>
                <div className="header_content d-flex flex-row align-items-center justify-content-start trans_400">
                    {/* Logo */}
                    <div className="logo">
                        <a href="/" onClick={(e) => { e.preventDefault(); trackCTA('logo_home', 'Header', 'scroll_to_top'); window.scrollTo(0, 0); }}>
                            <div>New<span>School</span></div>
                            <div>Rabia SÖNMEZ ALTUNTAS</div>
                        </a>
                    </div>

                    {/* Main Navigation */}
                    <nav className="main_nav">
                        <ul className="d-flex flex-row align-items-center justify-content-start">
                            <li className="active">
                                <a href="/" onClick={(e) => { e.preventDefault(); trackCTA('nav_accueil', 'Header', 'scroll_to_top'); window.scrollTo(0, 0); }}>
                                    Accueil
                                </a>
                            </li>
                            <li>
                                <a href="#Formationsprofessionnelles" onClick={(e) => { e.preventDefault(); trackCTA('nav_catalogues', 'Header', 'scroll_to_catalogues'); handleLinkClick('#Formationsprofessionnelles'); }}>
                                    Nos catalogues
                                </a>
                            </li>
                            <li>
                                <a href="#faq" onClick={(e) => { e.preventDefault(); trackCTA('nav_faq', 'Header', 'scroll_to_faq'); handleLinkClick('#faq'); }}>
                                    FAQ
                                </a>
                            </li>
                            <li>
                                <a href="#nouscontacter" onClick={(e) => { e.preventDefault(); trackCTA('nav_contact', 'Header', 'scroll_to_contact'); handleLinkClick('#nouscontacter'); }}>
                                    Nous contacter
                                </a>
                            </li>
                        </ul>
                    </nav>

                    <div className="header_extra d-flex flex-row align-items-center justify-content-end ml-auto">
                        {/* Header Social */}
                        <div className="social header_social">
                            <ul className="d-flex flex-row align-items-center justify-content-start">
                                <li>
                                    <a href="https://www.instagram.com/new_school_formation/" target="_blank" rel="noopener noreferrer" title="Instagram" onClick={() => trackOutboundClick('instagram_header', 'Header', 'https://www.instagram.com/new_school_formation/')}>
                                        <i className="fa fa-instagram" aria-hidden="true"></i>
                                    </a>
                                </li>
                                <li>
                                    <a href="https://www.facebook.com/newschool42/" target="_blank" rel="noopener noreferrer" title="Facebook" onClick={() => trackOutboundClick('facebook_header', 'Header', 'https://www.facebook.com/newschool42/')}>
                                        <i className="fa fa-facebook" aria-hidden="true"></i>
                                    </a>
                                </li>
                                {/* TikTok */}
                                <li className="social-icon-tiktok">
                                    <a href="https://www.tiktok.com/@rabifromnewschool" target="_blank" rel="noopener noreferrer" title="TikTok" aria-label="TikTok NewSchool" onClick={() => trackOutboundClick('tiktok_header', 'Header', 'https://www.tiktok.com/@rabifromnewschool')}>
                                        <img src={tiktokLogo} alt="TikTok" className="tiktok-icon" loading="lazy" decoding="async" width="28" height="28" />
                                    </a>
                                </li>
                            </ul>
                        </div>

                        {/* Hamburger */}
                        <button
                            type="button"
                            className="hamburger"
                            aria-label="Ouvrir le menu"
                            aria-controls="mobile-menu"
                            aria-expanded={isMobileMenuOpen}
                            onClick={toggleMobileMenu}
                        >
                            <i className="fa fa-bars" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Menu */}
            <div
                className={`menu_overlay trans_400 ${isMobileMenuOpen ? 'active' : ''}`}
                onClick={toggleMobileMenu}
            ></div>
            <div
                id="mobile-menu"
                className={`menu trans_400 ${isMobileMenuOpen ? 'active' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label="Menu mobile"
            >
                <div className="menu_close_container">
                    <button
                        type="button"
                        className="menu_close"
                        aria-label="Fermer le menu"
                        onClick={toggleMobileMenu}
                    >
                        <div></div>
                        <div></div>
                    </button>
                </div>
                <nav className="menu_nav">
                    <ul>
                        <li className="active">
                            <a href="/" onClick={(e) => { e.preventDefault(); trackCTA('mobile_nav_accueil', 'Header_Mobile', 'scroll_to_top'); window.scrollTo(0, 0); setIsMobileMenuOpen(false); }}>
                                Accueil
                            </a>
                        </li>
                        <li>
                            <a href="#Formationsprofessionnelles" onClick={(e) => { e.preventDefault(); trackCTA('mobile_nav_catalogues', 'Header_Mobile', 'scroll_to_catalogues'); handleLinkClick('#Formationsprofessionnelles'); }}>
                                Nos catalogues
                            </a>
                        </li>
                        <li>
                            <a href="#faq" onClick={(e) => { e.preventDefault(); trackCTA('mobile_nav_faq', 'Header_Mobile', 'scroll_to_faq'); handleLinkClick('#faq'); }}>
                                FAQ
                            </a>
                        </li>
                        <li>
                            <a href="#nouscontacter" onClick={(e) => { e.preventDefault(); trackCTA('mobile_nav_contact', 'Header_Mobile', 'scroll_to_contact'); handleLinkClick('#nouscontacter'); }}>
                                Nous contacter
                            </a>
                        </li>
                    </ul>
                </nav>
                <div className="menu_extra">
                    <div className="menu_link">Du lundi au jeudi : 10h-20h</div>
                    <div className="menu_link">09 53 79 80 66<br />07 67 42 88 60</div>
                    <div className="menu_link">
                        <a href="#form" onClick={(e) => { e.preventDefault(); trackCTA('mobile_prendre_rdv', 'Header_Mobile', 'scroll_to_form'); handleLinkClick('#form'); }}>
                            Prendre un rendez-vous
                        </a>
                    </div>
                </div>
                <div className="social menu_social">
                    <ul className="d-flex flex-row align-items-center justify-content-start">
                        <li>
                            <a href="https://www.instagram.com/new_school_formation/" target="_blank" rel="noopener noreferrer" title="Instagram" onClick={() => trackOutboundClick('instagram_mobile', 'Header_Mobile', 'https://www.instagram.com/new_school_formation/')}>
                                <i className="fa fa-instagram" aria-hidden="true"></i>
                            </a>
                        </li>
                        <li>
                            <a href="https://www.facebook.com/newschool42/" target="_blank" rel="noopener noreferrer" title="Facebook" onClick={() => trackOutboundClick('facebook_mobile', 'Header_Mobile', 'https://www.facebook.com/newschool42/')}>
                                <i className="fa fa-facebook" aria-hidden="true"></i>
                            </a>
                        </li>
                        {/* TikTok */}
                        <li className="social-icon-tiktok">
                            <a href="https://www.tiktok.com/@rabifromnewschool" target="_blank" rel="noopener noreferrer" title="TikTok" aria-label="TikTok NewSchool" onClick={() => trackOutboundClick('tiktok_mobile', 'Header_Mobile', 'https://www.tiktok.com/@rabifromnewschool')}>
                                <img src={tiktokLogo} alt="TikTok" className="tiktok-icon" loading="lazy" decoding="async" width="28" height="28" />
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </>
    );
};

export default Header;
